/* Visiting card — the QR code opens a static vCard, so the phone offers "Add contact".
   Fully client-side: static files only, no server, no secrets. Text is rendered as React nodes (never innerHTML). */

import { useEffect, useState, useCallback } from "react";
import QRCode from "qrcode";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const G = "#C8A96E";
const GA = (a) => `rgba(200,169,110,${a})`;
const DARK = "#050607";
const PAPER = "#F4EEDF";
const SANS = "'Inter',sans-serif";
const DISPLAY = "'Space Grotesk',sans-serif";

const PROFILE = {
  name: "Dev Gupta",
  title: "Senior Software Engineer",
  tagline: "AI Agents · Full Stack · Three.js",
  email: "devporwal00@gmail.com",
  phone: "+91 95829 53281",
  web: "devgupta.dev",
  linkedin: "linkedin.com/in/-devgupta",
  github: "github.com/Devgupta0",
  city: "Noida, India",
};

/* Absolute URL of the static vCard. Scanning the QR opens it and the phone offers "Add contact". */
const vcfUrl = () => `${window.location.origin}${process.env.PUBLIC_URL || ""}/dev-gupta.vcf`;

const qrOptions = { errorCorrectionLevel: "M", margin: 1, color: { dark: DARK, light: PAPER } };

const label = { fontFamily: SANS, fontSize: "9px", letterSpacing: "0.32em", textTransform: "uppercase", color: G };

function SectionHead({ eyebrow, children }) {
  return (
    <>
      <div style={{ ...label, marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ width: "36px", height: "1px", background: G, flexShrink: 0 }} />{eyebrow}
      </div>
      <h2 style={{ fontFamily: DISPLAY, fontSize: "clamp(32px,4vw,52px)", fontWeight: 900, color: "#F5F5F7", lineHeight: 1.05, marginBottom: "44px" }}>{children}</h2>
    </>
  );
}

const btn = (solid) => ({
  fontFamily: SANS, fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700,
  padding: "14px 26px", cursor: "pointer", textDecoration: "none", display: "inline-block", transition: "all 0.25s",
  background: solid ? G : "transparent", color: solid ? DARK : G, border: `1px solid ${solid ? G : GA(0.35)}`,
});

/* ══════════════ PNG export (print-ready, 3.5×2 in @ 300 dpi) ══════════════ */
async function renderCardPng(qrUrl) {
  const W = 1050, H = 600;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const x = c.getContext("2d");
  try { await Promise.all(["700 52px 'Space Grotesk'", "400 24px 'Inter'", "600 20px 'Inter'"].map((f) => document.fonts.load(f))); } catch { /* fall back to system fonts */ }

  const bg = x.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#0B0C0E"); bg.addColorStop(1, "#050607");
  x.fillStyle = bg; x.fillRect(0, 0, W, H);
  const glow = x.createRadialGradient(W * 0.82, H * 0.25, 10, W * 0.82, H * 0.25, 420);
  glow.addColorStop(0, "rgba(200,169,110,0.16)"); glow.addColorStop(1, "rgba(200,169,110,0)");
  x.fillStyle = glow; x.fillRect(0, 0, W, H);
  x.strokeStyle = GA(0.55); x.lineWidth = 2; x.strokeRect(24, 24, W - 48, H - 48);
  x.strokeStyle = GA(0.18); x.lineWidth = 1; x.strokeRect(36, 36, W - 72, H - 72);

  x.fillStyle = G; x.font = "700 40px 'Space Grotesk',sans-serif"; x.fillText("DG", 76, 112);
  x.fillRect(76, 128, 46, 3);
  x.fillStyle = "#F5F5F7"; x.font = "700 62px 'Space Grotesk',sans-serif"; x.fillText(PROFILE.name, 76, 228);
  x.fillStyle = G; x.font = "600 22px 'Inter',sans-serif";
  x.fillText(PROFILE.title.toUpperCase().split("").join(" "), 76, 270);
  x.fillStyle = GA(0.6); x.font = "400 22px 'Inter',sans-serif"; x.fillText(PROFILE.tagline, 76, 306);

  x.fillStyle = "#D8D8DE"; x.font = "400 25px 'Inter',sans-serif";
  [PROFILE.email, PROFILE.phone, PROFILE.web, PROFILE.linkedin, PROFILE.city].forEach((t, i) => {
    x.fillStyle = G; x.fillRect(76, 372 + i * 44 - 14, 8, 8);
    x.fillStyle = "#D8D8DE"; x.fillText(t, 100, 372 + i * 44);
  });

  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = qrUrl; });
  const s = 290, qx = W - 76 - s - 24, qy = 110;
  x.fillStyle = PAPER; x.beginPath(); x.roundRect(qx - 24, qy - 24, s + 48, s + 48, 18); x.fill();
  x.imageSmoothingEnabled = false; x.drawImage(img, qx, qy, s, s);
  x.fillStyle = G; x.font = "600 18px 'Inter',sans-serif"; x.textAlign = "center";
  x.fillText("SCAN · SAVE CONTACT", qx + s / 2, qy + s + 74);
  x.textAlign = "start";
  return c.toDataURL("image/png");
}

/* ══════════════ VISITING CARD ══════════════ */
export function VisitingCard({ isMobile }) {
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 140, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 140, damping: 16 });
  const sheen = useTransform(mx, [-0.5, 0.5], ["0%", "100%"]);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(vcfUrl(), { ...qrOptions, width: 480 }).then((u) => alive && setQr(u)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  const downloadPng = useCallback(async () => {
    if (!qr || busy) return;
    setBusy(true);
    try {
      const url = await renderCardPng(qr);
      const a = document.createElement("a");
      a.href = url; a.download = "dev-gupta-visiting-card.png"; a.click();
    } finally { setBusy(false); }
  }, [qr, busy]);

  const rows = [["✉", PROFILE.email], ["☎", PROFILE.phone], ["◎", PROFILE.web], ["in", PROFILE.linkedin], ["⌖", PROFILE.city]];

  return (
    <section id="card" style={{ padding: isMobile ? "60px 6vw 70px" : "80px 8vw 90px", background: DARK, borderTop: `1px solid ${GA(0.06)}` }}>
      <SectionHead eyebrow="digital.card">Scan. <span style={{ color: G, fontStyle: "italic" }}>Save</span> me.</SectionHead>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "36px" : "64px", alignItems: "center" }}>
        <div style={{ perspective: "1200px", width: "100%", maxWidth: "560px", flexShrink: 0 }}>
          <motion.div onMouseMove={onMove} onMouseLeave={reset}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", aspectRatio: "1.75 / 1", position: "relative", overflow: "hidden",
              background: "linear-gradient(135deg,#0D0E11 0%,#050607 70%)", border: `1px solid ${GA(0.5)}`, boxShadow: `0 30px 80px rgba(0,0,0,0.65), 0 0 60px ${GA(0.1)}`,
              padding: isMobile ? "5.5%" : "6%", display: "flex", justifyContent: "space-between", gap: "4%" }}>
            <motion.div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `linear-gradient(105deg,transparent 35%,${GA(0.14)} 50%,transparent 65%)`, backgroundSize: "250% 100%", backgroundPositionX: sheen }} />
            <div aria-hidden style={{ position: "absolute", inset: "7px", border: `1px solid ${GA(0.16)}`, pointerEvents: "none" }} />
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0, position: "relative" }}>
              <div>
                <div style={{ fontFamily: DISPLAY, fontWeight: 700, color: G, fontSize: "clamp(16px,3.4vw,22px)" }}>DG<div style={{ width: "26px", height: "2px", background: G, marginTop: "5px" }} /></div>
                <div style={{ fontFamily: DISPLAY, fontWeight: 700, color: "#F5F5F7", fontSize: "clamp(20px,5vw,32px)", lineHeight: 1.05, marginTop: "clamp(10px,2.4vw,18px)" }}>{PROFILE.name}</div>
                <div style={{ ...label, letterSpacing: "0.2em", fontSize: "clamp(7px,1.6vw,10px)", marginTop: "8px" }}>{PROFILE.title}</div>
                <div style={{ fontFamily: SANS, color: GA(0.55), fontSize: "clamp(8px,1.8vw,11px)", marginTop: "5px" }}>{PROFILE.tagline}</div>
              </div>
              <div style={{ display: "grid", gap: "clamp(2px,0.7vw,5px)" }}>
                {rows.map(([ic, v]) => (
                  <div key={v} style={{ fontFamily: SANS, fontSize: "clamp(8px,1.9vw,11.5px)", color: "#CFCFD6", display: "flex", gap: "8px", whiteSpace: "nowrap" }}>
                    <span style={{ color: G, width: "12px", textAlign: "center" }}>{ic}</span>{v}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ alignSelf: "center", textAlign: "center", flexShrink: 0, width: "34%", position: "relative", transform: "translateZ(30px)" }}>
              <div style={{ background: PAPER, padding: "6%", borderRadius: "8px", aspectRatio: "1", display: "grid", placeItems: "center" }}>
                {qr ? <img src={qr} alt="QR code that saves Dev Gupta's contact card" style={{ width: "100%", imageRendering: "pixelated", display: "block" }} /> : null}
              </div>
              <div style={{ ...label, fontSize: "clamp(6px,1.3vw,8px)", letterSpacing: "0.18em", marginTop: "8px" }}>Scan · Save contact</div>
            </div>
          </motion.div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: SANS, fontSize: "13px", lineHeight: 2, color: GA(0.6), maxWidth: "440px", marginBottom: "28px" }}>
            Point your phone camera at the QR code. It opens my contact card and your phone offers to add it to your address book. No app needed.
          </p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <a href={`${process.env.PUBLIC_URL || ""}/dev-gupta.vcf`} download="Dev-Gupta.vcf" style={btn(true)}>Save contact</a>
            <button onClick={downloadPng} disabled={!qr || busy} style={{ ...btn(false), opacity: qr ? 1 : 0.5 }}>{busy ? "Preparing…" : "Download card PNG"}</button>
          </div>
        </div>
      </div>
    </section>
  );
}
